import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const MANUAL_URL = `${BASE}/manual`;

const api = axios.create({ baseURL: BASE });

// Auto-attach the admin (CRM) token to requests that don't already set one.
// Portal/client calls pass their own token explicitly (authHeader) and are untouched,
// and unauthenticated public calls (e.g. the Contact form POST /leads) send no header.
api.interceptors.request.use((config) => {
  if (!config.headers?.Authorization) {
    const t = localStorage.getItem("rfl_crm_token");
    if (t) config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// Products
export const fetchProducts = () => api.get("/products").then((r) => r.data);
export const createProduct = (data) => api.post("/products", data).then((r) => r.data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data).then((r) => r.data);
export const deleteProduct = (id) => api.delete(`/products/${id}`).then((r) => r.data);

// Invoices
export const fetchInvoices = (clientEmail) =>
  api.get("/invoices", { params: clientEmail ? { client_email: clientEmail } : {} }).then((r) => r.data);
export const fetchInvoice = (id) => api.get(`/invoices/${id}`).then((r) => r.data);
export const createInvoice = (data) => api.post("/invoices", data).then((r) => r.data);
export const updateInvoice = (id, data) => api.put(`/invoices/${id}`, data).then((r) => r.data);
export const deleteInvoice = (id) => api.delete(`/invoices/${id}`).then((r) => r.data);

// Payments (PayPal)
export const fetchPaymentConfig = () => api.get("/payments/config").then((r) => r.data);
export const createPaypalOrder = (invoice_id) =>
  api.post("/payments/paypal/create-order", { invoice_id }).then((r) => r.data);
export const capturePaypalOrder = (invoice_id, order_id) =>
  api.post("/payments/paypal/capture", { invoice_id, order_id }).then((r) => r.data);
export const resendReceipt = (token, invoice_id, email) =>
  api.post(`/invoices/${invoice_id}/resend-receipt`, { email }, authHeader(token)).then((r) => r.data);

// Settings
export const fetchSettings = () => api.get("/settings").then((r) => r.data);
export const updateSettings = (data) => api.put("/settings", data).then((r) => r.data);

// ---- Auth (token passed explicitly to avoid client/admin token mixups) ----
const authHeader = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

export const authRegister = (data) => api.post("/auth/register", data).then((r) => r.data);
export const authLogin = (data) => api.post("/auth/login", data).then((r) => r.data);
export const forgotPassword = (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data);
export const resetPassword = (token, password) => api.post("/auth/reset-password", { token, password }).then((r) => r.data);
export const adminSetUserPassword = (token, id, password) =>
  api.put(`/users/${id}/password`, { password }, authHeader(token)).then((r) => r.data);
export const authMe = (token) => api.get("/auth/me", authHeader(token)).then((r) => r.data);
export const authUpdateProfile = (token, data) =>
  api.put("/auth/profile", data, authHeader(token)).then((r) => r.data);
export const authChangeEmail = (token, email, current_password) =>
  api.put("/auth/email", { email, current_password }, authHeader(token)).then((r) => r.data);
export const authChangePassword = (token, current_password, new_password) =>
  api.put("/auth/password", { current_password, new_password }, authHeader(token)).then((r) => r.data);

// Users (admin)
export const listUsers = (token, role) =>
  api.get("/users", { ...authHeader(token), params: role ? { role } : {} }).then((r) => r.data);
export const createUserApi = (token, data) => api.post("/users", data, authHeader(token)).then((r) => r.data);
export const updateUserAccess = (token, id, data) =>
  api.put(`/users/${id}/access`, data, authHeader(token)).then((r) => r.data);
export const deleteUserApi = (token, id) => api.delete(`/users/${id}`, authHeader(token)).then((r) => r.data);
export const impersonateUser = (token, id) =>
  api.post(`/users/${id}/impersonate`, {}, authHeader(token)).then((r) => r.data);

// Activity
export const getMyActivity = (token) => api.get("/activity/me", authHeader(token)).then((r) => r.data);
export const getClientActivity = (token, email) =>
  api.get("/activity/client", { ...authHeader(token), params: { email } }).then((r) => r.data);
export const getGlobalActivity = (token) => api.get("/activity", authHeader(token)).then((r) => r.data);
export const pushToDisputeFox = (token, person) =>
  api.post("/integrations/disputefox/push", person, authHeader(token)).then((r) => r.data);

// Messaging
export const getMyConversation = (token) => api.get("/messages/me", authHeader(token)).then((r) => r.data);
export const sendMyMessage = (token, text) => api.post("/messages/me", { text }, authHeader(token)).then((r) => r.data);
export const getConversations = (token) => api.get("/conversations", authHeader(token)).then((r) => r.data);
export const adminReply = (token, userId, text) =>
  api.post(`/conversations/${userId}/reply`, { text }, authHeader(token)).then((r) => r.data);
export const getMessageContacts = (token) =>
  api.get("/conversations/contacts", authHeader(token)).then((r) => r.data);
export const startConversation = (token, userId) =>
  api.post(`/conversations/${userId}/start`, {}, authHeader(token)).then((r) => r.data);

// Leads
export const fetchLeads = () => api.get("/leads").then((r) => r.data);
export const createLead = (data) => api.post("/leads", data).then((r) => r.data);
export const updateLeadApi = (id, data) => api.put(`/leads/${id}`, data).then((r) => r.data);
export const deleteLeadApi = (id) => api.delete(`/leads/${id}`).then((r) => r.data);

// Clients
export const fetchClients = () => api.get("/clients").then((r) => r.data);
export const createClient = (data) => api.post("/clients", data).then((r) => r.data);
export const updateClientApi = (id, data) => api.put(`/clients/${id}`, data).then((r) => r.data);
export const addClientNoteApi = (id, text) => api.post(`/clients/${id}/notes`, { text }).then((r) => r.data);
export const deleteClientApi = (id) => api.delete(`/clients/${id}`).then((r) => r.data);

// Appointments
export const fetchAppointments = () => api.get("/appointments").then((r) => r.data);
export const createAppointment = (data) => api.post("/appointments", data).then((r) => r.data);
export const updateAppointmentApi = (id, data) => api.put(`/appointments/${id}`, data).then((r) => r.data);
export const deleteAppointmentApi = (id) => api.delete(`/appointments/${id}`).then((r) => r.data);

// Notes (admin) — client notes with voice transcription + AI summary
export const fetchNotes = (token, clientId) =>
  api.get("/notes", { ...authHeader(token), params: clientId ? { client_id: clientId } : {} }).then((r) => r.data);
export const createNote = (token, data) => api.post("/notes", data, authHeader(token)).then((r) => r.data);
export const updateNote = (token, id, data) => api.put(`/notes/${id}`, data, authHeader(token)).then((r) => r.data);
export const deleteNote = (token, id) => api.delete(`/notes/${id}`, authHeader(token)).then((r) => r.data);
export const transcribeAudio = (token, blob) => {
  const form = new FormData();
  form.append("file", blob, "note.webm");
  return api.post("/notes/transcribe", form, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const summarizeNotes = (token, payload) =>
  api.post("/notes/summarize", payload, authHeader(token)).then((r) => r.data);

// RingCentral click-to-call
export const ringcentralStatus = (token) => api.get("/ringcentral/status", authHeader(token)).then((r) => r.data);
export const ringOut = (token, payload) => api.post("/calls/ringout", payload, authHeader(token)).then((r) => r.data);
export const importCallRecording = (token, payload) => api.post("/calls/import-recording", payload, authHeader(token)).then((r) => r.data);

// Booking (client self-service)
export const fetchBookingConfig = () => api.get("/booking/config").then((r) => r.data);
export const fetchBookingSlots = (token, date) =>
  api.get(`/booking/slots?date=${date}`, authHeader(token)).then((r) => r.data);
export const createBooking = (token, data) => api.post("/booking", data, authHeader(token)).then((r) => r.data);
export const fetchMyBookings = (token) => api.get("/booking/mine", authHeader(token)).then((r) => r.data);
export const cancelMyBooking = (token, id) => api.put(`/booking/${id}/cancel`, {}, authHeader(token)).then((r) => r.data);

// Documents
export const uploadDocument = (token, file, requirementId) => {
  const form = new FormData();
  form.append("file", file);
  if (requirementId) form.append("requirement_id", requirementId);
  return api.post("/documents", form, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const fetchMyDocuments = (token) => api.get("/documents/me", authHeader(token)).then((r) => r.data);
export const fetchAllDocuments = (token) => api.get("/documents", authHeader(token)).then((r) => r.data);
export const fetchUnreviewedCount = (token) => api.get("/documents/unreviewed-count", authHeader(token)).then((r) => r.data);
export const reviewDocument = (token, id, status, admin_note) =>
  api.put(`/documents/${id}/review`, { status, admin_note }, authHeader(token)).then((r) => r.data);
export const deleteDocument = (token, id) => api.delete(`/documents/${id}`, authHeader(token)).then((r) => r.data);
export const documentDownloadUrl = (token, id) => `${BASE}/documents/${id}/download?auth=${encodeURIComponent(token)}`;

// Required documents checklist
export const fetchRequirements = (token) => api.get("/document-requirements", authHeader(token)).then((r) => r.data);
export const createRequirement = (token, label, description) =>
  api.post("/document-requirements", { label, description }, authHeader(token)).then((r) => r.data);
export const deleteRequirement = (token, id) => api.delete(`/document-requirements/${id}`, authHeader(token)).then((r) => r.data);
export const remindClientDocuments = (token, userId) =>
  api.post(`/clients/${userId}/document-reminder`, {}, authHeader(token)).then((r) => r.data);

export const formatDate = (val) => {
  if (!val) return "";
  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}/.test(val)) {
    const [y, m, d] = val.slice(0, 10).split("-");
    return `${m}/${d}/${y}`;
  }
  const dt = new Date(val);
  if (isNaN(dt.getTime())) return val;
  return `${String(dt.getMonth() + 1).padStart(2, "0")}/${String(dt.getDate()).padStart(2, "0")}/${dt.getFullYear()}`;
};

export const formatDateTime = (val) => {
  if (!val) return "";
  const dt = new Date(val);
  if (isNaN(dt.getTime())) return val;
  const date = `${String(dt.getMonth() + 1).padStart(2, "0")}/${String(dt.getDate()).padStart(2, "0")}/${dt.getFullYear()}`;
  let h = dt.getHours();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${date} ${h}:${String(dt.getMinutes()).padStart(2, "0")} ${ampm}`;
};


export const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

// Format US phone input to (555) 123-4567 as the user types.
export const formatUSPhone = (val) => {
  const d = (val || "").replace(/\D/g, "").slice(0, 10);
  if (d.length === 0) return "";
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

// Books (digital downloads)
export const fetchBooks = () => api.get("/books").then((r) => r.data);
export const fetchAllBooks = (token) => api.get("/books/all", authHeader(token)).then((r) => r.data);
export const createBook = (token, data) => api.post("/books", data, authHeader(token)).then((r) => r.data);
export const updateBook = (token, id, data) => api.put(`/books/${id}`, data, authHeader(token)).then((r) => r.data);
export const deleteBook = (token, id) => api.delete(`/books/${id}`, authHeader(token)).then((r) => r.data);
export const uploadBookFile = (token, id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/books/${id}/file`, fd, authHeader(token)).then((r) => r.data);
};
export const fetchBookPurchases = (token) => api.get("/books/purchases", authHeader(token)).then((r) => r.data);
export const createBookOrder = (book_id) => api.post("/books/paypal/create-order", { book_id }).then((r) => r.data);
export const captureBookOrder = (book_id, order_id) => api.post("/books/paypal/capture", { book_id, order_id }).then((r) => r.data);
export const bookDownloadUrl = (token) => `${BASE}/books/download/${token}`;
export const fetchBookBundle = () => api.get("/books/bundle").then((r) => r.data);
export const updateBookBundle = (token, data) => api.put("/books/bundle/config", data, authHeader(token)).then((r) => r.data);
export const createBundleOrder = () => api.post("/books/bundle/paypal/create-order").then((r) => r.data);
export const captureBundleOrder = (order_id) => api.post("/books/bundle/paypal/capture", { order_id }).then((r) => r.data);

export default api;
